import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Modal } from '@safetyhub/ui';
// 조직 처분 어휘는 web·server와 같은 enum 하나를 본다
// (shared/types/org-withdrawal-action.ts — 원래 이 앱의 서버 DTO에 있던 것이 정본으로 승격됐다)
import { OrgWithdrawalAction, WithdrawalReasonType } from '@safetyhub/shared';
import { adminApi, extractApiError } from '../lib/api';
import { OWNER_LABEL } from '../lib/roles';

interface User {
  id: string;
  member: { name: string; phone: string | null } | null;
  deletionSource: 'SELF' | 'ADMIN' | null;
  memberships: Array<{
    /** 소유자 성질(배정 축, P7-b-2 — 옛 `role === 'OWNER'`) */
    isOwner: boolean;
    /** ⚠️ false = 외부 연락처(현장 담당자·외부 결재자). 조직 권한 없음 — `api.ts` 주석 참조 */
    countsAsMember: boolean;
    organization: { id: string; name: string; category: string };
  }>;
}

/** 강제 탈퇴 다이얼로그가 성공 시 부모에게 넘기는 결과 */
export interface DeleteUserResult {
  /** 운영자가 본인 통지를 요청했는가 */
  notifyRequested?: boolean;
  /** 실제로 나갔는가 — 요청했는데 false면 연락처 없음·발송 실패다 */
  notified?: boolean;
}

interface DeleteUserDialogProps {
  user: User | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (result: DeleteUserResult) => void;
}

/**
 * 강제 탈퇴 사유 유형 — 고르는 값이 **복원·재가입 가부를 바꾼다**(결정 기록 2026-08-20 정책 8).
 * 라벨만 보고 고르면 안 되므로 각 항목이 결과를 그대로 말한다.
 */
const REASON_TYPE_OPTIONS: Array<{
  value: WithdrawalReasonType;
  label: string;
  hint: string;
}> = [
  {
    value: WithdrawalReasonType.SANCTION,
    label: '제재 (약관 위반 등)',
    hint: '본인 복원 불가 · 같은 전화번호로 일정 기간 재가입 차단',
  },
  {
    value: WithdrawalReasonType.CLEANUP,
    label: '정리 (테스트 데이터 · 사용자 요청 대행)',
    hint: '본인이 로그인해 복원 가능 · 재가입 자유',
  },
];

interface OrgConfig {
  action: OrgWithdrawalAction;
  /** 이관 대상 — Member.id(사람). 조직 밖 계정·퇴사자도 가리킬 수 있는 축이다 */
  transferToMemberId?: string;
  /**
   * 조직 **밖에서** 검색으로 고른 대상의 표시 정보.
   *
   * 내부 멤버 드롭다운(`members`)에는 없는 사람이라 id만 들고 있으면 화면에 이름이
   * 안 나온다 — 운영자가 누구에게 넘기는지 못 보는 채로 확인을 누르게 된다.
   * 내부 드롭다운으로 다시 고르면 이 값은 지워진다(두 경로가 같은 필드를 다투지 않게).
   */
  externalTarget?: TransferCandidate;
}

/** 이관 대상 후보 — 서버 `/admin/users/transfer-target-candidates` 응답 1건 */
type TransferCandidate = NonNullable<
  Awaited<ReturnType<typeof adminApi.searchTransferTargetCandidates>>['data']
>[number];

/** 후보 한 명을 한 줄로 — 동명이인을 가르는 단서(아이디/이메일/번호/소속)를 붙인다 */
function candidateHint(c: TransferCandidate): string {
  const parts = [c.username, c.email, c.phone].filter(Boolean) as string[];
  const orgs = c.organizations.map((o) => o.name).join(', ');
  if (orgs) parts.push(`소속: ${orgs}`);
  else parts.push('소속 조직 없음');
  return parts.join(' · ');
}

/**
 * 조직 **밖** 계정을 이관 대상으로 지정하는 검색 (S15).
 *
 * 조직 안에 넘길 사람이 아무도 없는 경우가 실재하고, 서버는 이미 그 이관을 받아들인다
 * (`memberId_companyId` upsert가 create 갈래로 새 멤버십을 만든다). 막고 있던 건 화면뿐이라
 * 여기서 그 길을 연다.
 *
 * 최소 검색어 길이는 **서버가 소유한다** — 상수를 여기 복제하면 한쪽만 바뀌어 어긋난다.
 * 짧은 질의는 서버가 DB를 건드리지 않고 빈 배열 + `meta.minQueryLength`로 돌려주고,
 * 이 컴포넌트는 그 값을 그대로 안내에 쓴다.
 */
function TransferTargetSearch({
  excludeUserId,
  selected,
  onPick,
  onClear,
}: {
  excludeUserId: string;
  selected?: TransferCandidate;
  onPick: (candidate: TransferCandidate) => void;
  onClear: () => void;
}) {
  const [term, setTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');

  // 개인정보 조회라 타이핑마다 쏘지 않는다
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedTerm(term.trim()), 300);
    return () => clearTimeout(timer);
  }, [term]);

  const searchQuery = useQuery({
    queryKey: ['admin', 'transfer-target-candidates', debouncedTerm, excludeUserId],
    queryFn: () =>
      adminApi.searchTransferTargetCandidates({ q: debouncedTerm, excludeUserId }),
    enabled: debouncedTerm.length > 0,
  });

  const candidates = searchQuery.data?.data ?? [];
  const minQueryLength = searchQuery.data?.meta?.minQueryLength;
  const truncated = searchQuery.data?.meta?.truncated ?? false;
  const tooShort =
    debouncedTerm.length > 0 &&
    minQueryLength !== undefined &&
    debouncedTerm.length < minQueryLength;

  if (selected) {
    return (
      <div className="ml-6 mt-2 flex items-center gap-2">
        <span className="text-sm text-gray-900">
          {selected.name}
          <span className="ml-2 text-xs text-gray-500">{candidateHint(selected)}</span>
        </span>
        <button
          type="button"
          onClick={onClear}
          className="text-xs text-gray-500 underline hover:text-gray-700"
        >
          변경
        </button>
      </div>
    );
  }

  return (
    <div className="ml-6 mt-2">
      <input
        type="text"
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        placeholder="다른 사용자 찾기 (아이디 · 이메일 · 이름 · 전화번호)"
        className="w-full max-w-sm px-3 py-2 text-sm border border-gray-300 rounded-control focus:ring-2 focus:ring-brand-500"
      />
      {tooShort && (
        <p className="mt-1 text-xs text-gray-500">
          {minQueryLength}자 이상 입력하세요.
        </p>
      )}
      {!tooShort && searchQuery.isFetching && (
        <p className="mt-1 text-xs text-gray-500">검색 중...</p>
      )}
      {!tooShort && !searchQuery.isFetching && debouncedTerm.length > 0 && (
        <>
          {candidates.length === 0 ? (
            <p className="mt-1 text-xs text-gray-500">
              일치하는 계정이 없습니다. 탈퇴한 계정과 계정 없는 외부 인원은 조직을 소유할 수
              없어 결과에서 제외됩니다.
            </p>
          ) : (
            <ul className="mt-2 max-h-48 overflow-y-auto border border-gray-200 rounded-control divide-y divide-gray-100">
              {candidates.map((c) => (
                <li key={c.memberId}>
                  <button
                    type="button"
                    onClick={() => onPick(c)}
                    className="w-full px-3 py-2 text-left hover:bg-gray-50"
                  >
                    <span className="text-sm text-gray-900">{c.name}</span>
                    <span className="block text-xs text-gray-500">{candidateHint(c)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {truncated && (
            <p className="mt-1 text-xs text-gray-500">
              결과가 많아 일부만 표시했습니다. 검색어를 더 좁혀주세요.
            </p>
          )}
        </>
      )}
    </div>
  );
}

export default function DeleteUserDialog({
  user,
  isOpen,
  onClose,
  onSuccess,
}: DeleteUserDialogProps) {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  // 기본값을 두지 않는다 — 어느 쪽으로 기울든 관리자가 고르지 않은 처분이 적용된다.
  const [reasonType, setReasonType] = useState<WithdrawalReasonType | ''>('');
  // 기본 꺼짐 — 이 옵션이 생기기 전 동작(무통지)이 기본값이다.
  const [notifyUser, setNotifyUser] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orgConfigs, setOrgConfigs] = useState<Record<string, OrgConfig>>({});

  // 소유한 **모든** 조직 — 판정 축은 소유자 성질(`isOwner`, 배정 축) 하나다.
  //
  // 예전엔 여기서 `category !== 'WORKSPACE'`를 걸러 서버 축과 갈라져 있었다.
  // `WORKSPACE`는 1인 전용이 아니라 "개인/팀 작업공간"이라 **멤버가 여럿인 작업공간**이
  // 실재하는데, 그 조직은 이 화면에 안 뜨면서 서버는 처분을 요구해
  // 400(ADMIN_WITHDRAW_ORG_ACTION_REQUIRED)이 나는 막다른 길이었다 — 운영자가 화면에서
  // 고를 방법이 없어 강제 탈퇴 자체가 불가능했다.
  //
  // 1인 작업공간까지 포함하는 이유는 **이관을 고를 수 있어야 하기 때문**이다. admin 이관을
  // 남긴 근거(소유자가 스스로 이전할 수 없는 경우 대행)는 조직 유형·멤버 수와 무관하다.
  const ownedOrgs = user?.memberships.filter((m) => m.isOwner) || [];

  // 각 조직의 멤버 목록 조회 (이관 대상 지정용)
  //
  // ⚠️ 여기 id는 Company.id다 — 예전엔 subscriptionId를 받는 getSubscriptionMembers에 넘겨
  //    항상 404가 났고, 그래서 이관 대상 목록이 늘 비어 "권한 이전"을 고를 수 없었다.
  const orgMembersQueries = useQuery({
    queryKey: ['admin', 'org-members', ownedOrgs.map((o) => o.organization.id)],
    queryFn: async () => {
      // 축은 Member.id 하나다 — CompanyMember.id는 하드삭제(레거시 manualOwnerId)만
      // 쓰던 축이고 그 경로는 P8에서 사라졌다.
      const results: Record<
        string,
        Array<{
          memberId: string;
          name: string;
          isOwner: boolean;
          leftAt: string | null;
        }>
      > = {};
      for (const org of ownedOrgs) {
        const response = await adminApi.getCompanyMembers(org.organization.id);
        if (response.success && response.data) {
          results[org.organization.id] = response.data
            // 이관 대상은 계정 있는 사람만 — 계정 없는 외부 인원은 서버가 거부한다
            .filter((m) => !!m.userId && !m.isDeactivated && m.userId !== user?.id)
            .map((m) => ({
              memberId: m.memberId,
              name: m.name,
              isOwner: m.isOwner,
              leftAt: m.leftAt,
            }));
        }
      }
      return results;
    },
    enabled: isOpen && ownedOrgs.length > 0,
  });

  /**
   * 이미 탈퇴 처리된 사용자.
   *
   * 하드삭제가 사라진 뒤로 이 상태에서 **할 수 있는 일이 없다** — 서버도
   * `forceWithdrawUser`를 400('이미 탈퇴된 사용자입니다')으로 거부한다. 그래서 요청을
   * 보내는 대신 지금 상태와 다음에 일어날 일을 알려주고 닫는다. 정상 흐름에서는 부모가
   * 이 다이얼로그를 열지 않고 '탈퇴 철회'만 노출하므로, 여기 걸리는 건 목록이 낡은 경우다.
   */
  const isAlreadyDeleted = !!user?.deletionSource;

  /**
   * 본인 통지가 **의미 있는** 유형인가.
   *
   * 통지 문구가 "30일 내 로그인하면 복구 가능"을 안내하는데, 그 말은 제재(SANCTION)에는
   * 거짓이다 — 본인 복원이 막혀 있어 안내대로 하면 로그인에서 튕긴다. 서버도 같은 축으로
   * 거른다(admin-user-management.service `isSelfRestorableWithdrawal` 근거).
   */
  const canNotify = reasonType === WithdrawalReasonType.CLEANUP;

  // 초기화 - 조직 멤버 데이터가 로드된 후 설정
  useEffect(() => {
    if (isOpen) {
      setReason('');
      setReasonType('');
      setNotifyUser(false);
      setError(null);
    }
  }, [isOpen, user?.id]);

  // 조직별 설정 초기화 (멤버 데이터 로드 후)
  useEffect(() => {
    if (isOpen && orgMembersQueries.data) {
      const configs: Record<string, OrgConfig> = {};
      ownedOrgs.forEach((o) => {
        const members = orgMembersQueries.data?.[o.organization.id] || [];
        // 다른 멤버가 없으면 CLOSE만 가능, 있으면 TRANSFER 기본값.
        // ⚠️ 1인 조직의 기본값이 CLOSE인 것은 **이 변경 전 동작 그대로**다 — 예전엔 1인 조직이
        //    화면에 뜨지도 않고 서버가 무조건 폐쇄했다. 멤버가 있는 조직의 TRANSFER 기본값도
        //    그대로 둔다: 대상 미선택이면 확인 버튼이 잠겨 운영자가 반드시 한 번 고르게 된다
        //    (기본값을 CLOSE로 바꾸면 아무것도 안 만진 채 조직이 닫힐 수 있다).
        configs[o.organization.id] = {
          action:
            members.length > 0
              ? OrgWithdrawalAction.TRANSFER
              : OrgWithdrawalAction.CLOSE,
        };
      });
      setOrgConfigs(configs);
    }
  }, [isOpen, orgMembersQueries.data]);

  // 강제 탈퇴 mutation
  const withdrawMutation = useMutation({
    mutationFn: () => {
      // 소유 조직 **전부**의 처분을 보낸다 — 예전엔 첫 조직 설정 하나만 보내
      // 나머지 조직이 이관도 폐쇄도 안 된 채 OWNER 0명으로 남았다.
      const orgActions: Record<
        string,
        { action: OrgWithdrawalAction; transferToMemberId?: string }
      > = {};
      for (const org of ownedOrgs) {
        const config = orgConfigs[org.organization.id];
        if (!config) continue;
        orgActions[org.organization.id] = {
          action: config.action,
          transferToMemberId:
            config.action === OrgWithdrawalAction.TRANSFER
              ? config.transferToMemberId
              : undefined,
        };
      }

      return adminApi.forceWithdrawUser(user!.id, {
        // handleSubmit이 빈 값을 막으므로 여기 도달하면 값이 있다
        reasonType: reasonType as WithdrawalReasonType,
        reason: reason || undefined,
        orgActions: Object.keys(orgActions).length > 0 ? orgActions : undefined,
        // 제재 유형에서는 서버가 어차피 보내지 않는다 — 체크가 남아 있어도 여기서 끈다
        notifyUser: canNotify && notifyUser,
      });
    },
    onSuccess: (data) => {
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
        // 통지를 켜고 눌렀는데 실제로 안 나갔으면(연락처 없음·발송 실패) 그 사실을 올린다 —
        // 성공 문구만 띄우면 운영자는 통지가 갔다고 믿는다.
        onSuccess({
          notifyRequested: canNotify && notifyUser,
          notified: data.data?.notified ?? false,
        });
        onClose();
      } else {
        setError(data.error?.message || '강제 탈퇴에 실패했습니다.');
      }
    },
    onError: (err) => {
      setError(extractApiError(err).message);
    },
  });

  const handleSubmit = () => {
    setError(null);

    // 소유 조직에 권한 이전을 골랐으면 대상이 지정됐는지 검증
    if (ownedOrgs.length > 0) {
      for (const org of ownedOrgs) {
        const config = orgConfigs[org.organization.id];
        if (config?.action === OrgWithdrawalAction.TRANSFER && !config.transferToMemberId) {
          setError(`${org.organization.name}의 새 Owner를 선택해주세요.`);
          return;
        }
      }
    }

    if (!reasonType) {
      setError('강제 탈퇴 사유 유형을 선택해주세요.');
      return;
    }

    withdrawMutation.mutate();
  };

  const updateOrgConfig = (orgId: string, updates: Partial<OrgConfig>) => {
    setOrgConfigs((prev) => ({
      ...prev,
      [orgId]: { ...prev[orgId], ...updates },
    }));
  };

  const isPending = withdrawMutation.isPending;

  // 모든 권한 이전 선택에 멤버가 지정되었는지 확인
  const isTransferMemberMissing = ownedOrgs.some((org) => {
    const config = orgConfigs[org.organization.id];
    return config?.action === OrgWithdrawalAction.TRANSFER && !config.transferToMemberId;
  });

  const isConfirmDisabled = !reasonType || isTransferMemberMissing;

  if (!user) return null;

  // 이미 탈퇴한 사용자 — 보낼 요청이 없다. 막다른 길로 두지 않고 다음 경로를 알려준다.
  if (isAlreadyDeleted) {
    return (
      <Modal open={isOpen} onClose={onClose} title="이미 탈퇴한 사용자" size="md">
        <div className="space-y-6">
          <div className="p-4 bg-orange-50 border border-orange-200 rounded-card">
            <p className="text-sm text-orange-800">
              <strong>{user.member?.name ?? ''}</strong> 사용자는 이미 탈퇴 처리되었습니다.
              <br />
              <span className="text-xs">
                (현재 상태:{' '}
                {user.deletionSource === 'SELF' ? '본인 탈퇴' : '관리자 강제 탈퇴'})
              </span>
            </p>
          </div>
          <div className="text-sm text-gray-600 space-y-2">
            <p>
              계정을 되살리려면 <strong>탈퇴 철회</strong>를 사용하세요. 사유 유형이 제재인
              경우 본인 로그인으로는 복구되지 않고 이 화면에서만 되돌릴 수 있습니다.
            </p>
            <p>
              되돌리지 않으면 탈퇴 30일 후 예약된 정리 작업이 개인정보를 파기합니다.
              계정 레코드를 즉시 지우는 기능은 없습니다 — 점검·보고서의 작성자 참조를
              끊지 않기 위해 삭제가 아니라 익명화를 정본으로 둡니다.
            </p>
          </div>
          <div className="flex justify-end pt-4 border-t">
            <Button variant="secondary" onClick={onClose}>
              닫기
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    // 처리 중에는 닫는 길(오버레이·Esc·✕) 전부를 막는다 — 응답 전에 닫히면 결과를 볼 자리가 사라진다.
    <Modal
      open={isOpen}
      onClose={onClose}
      title="사용자 강제 탈퇴"
      size="md"
      closeOnOverlay={!isPending}
      closeOnEsc={!isPending}
      showCloseButton={!isPending}
      closeLocked={isPending}
    >
      <div className="space-y-6">
        {/* 경고 메시지 */}
        <div className="p-4 bg-red-50 border border-red-200 rounded-card">
          <div className="flex items-start">
            <svg
              className="w-5 h-5 text-red-600 mt-0.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">주의</h3>
              <p className="mt-1 text-sm text-red-700">
                <strong>{user.member?.name ?? ''}</strong> 사용자를 강제 탈퇴 처리합니다.
              </p>
              <p className="mt-1 text-xs text-red-700">
                로그인이 차단되고 모든 멤버십이 해제됩니다. 슈퍼관리자가 복원할 수 있고,
                30일 뒤에는 예약된 정리 작업이 개인정보를 파기합니다.
              </p>
            </div>
          </div>
        </div>

        {/* 소유 조직 처리 — 조직 유형·멤버 수와 무관하게 소유한 모든 조직 */}
        {ownedOrgs.length > 0 && (
          <div>
            <label className="text-sm font-medium text-gray-700">
              소유한 조직 처리
            </label>
            <p className="text-xs text-gray-500 mt-1">
              강제 탈퇴된 사용자는 조직을 관리할 수 없습니다. 개인/팀 작업공간도 포함해
              조직마다 이관 또는 폐쇄를 지정하세요.
            </p>
            <div className="mt-3 space-y-4">
              {ownedOrgs.map((org) => {
                const config = orgConfigs[org.organization.id] || {
                  action: OrgWithdrawalAction.CLOSE,
                };
                const members = orgMembersQueries.data?.[org.organization.id] || [];
                const hasMembers = members.length > 0;

                return (
                  <Card key={org.organization.id} surface="soft" elevation="none">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-medium text-sm text-gray-900">
                        {org.organization.name}
                        {/* 유형을 밝힌다 — 목록에 개인/팀 작업공간이 섞이므로 */}
                        <span className="ml-2 text-xs font-normal text-gray-500">
                          {org.organization.category === 'WORKSPACE'
                            ? '개인/팀'
                            : '비즈니스'}
                        </span>
                      </span>
                      <span className="text-xs text-gray-500">
                        멤버 {members.length}명
                      </span>
                    </div>
                    <div className="space-y-3">
                      {/*
                        권한 이전 옵션.

                        ⚠️ 예전엔 조직 내부 멤버가 없으면 이 라디오를 `disabled`로 잠갔다.
                        정책(결정 기록 2026-08-20 정책 10 · S15)은 admin 이관 대상을
                        "조직 내 멤버 ∪ 조직 밖 계정"으로 정하고 서버도 그렇게 동작하는데
                        (소속 제약 없는 Member 조회 + memberId_companyId upsert의 create
                        갈래), 화면만 막고 있어 **조직 안에 아무도 없는 계정은 폐쇄 외에
                        선택지가 없었다**. 이제 검색으로 조직 밖 계정을 지정할 수 있으므로
                        이 라디오는 **항상** 고를 수 있어야 한다.
                      */}
                      <div>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name={`org-${org.organization.id}`}
                            checked={config.action === OrgWithdrawalAction.TRANSFER}
                            onChange={() =>
                              updateOrgConfig(org.organization.id, {
                                action: OrgWithdrawalAction.TRANSFER,
                                transferToMemberId: undefined,
                                externalTarget: undefined,
                              })
                            }
                            className="text-brand-600"
                          />
                          <span className="ml-2 text-sm text-gray-700">권한 이전</span>
                          {!hasMembers && (
                            <span className="ml-2 text-xs text-gray-500">
                              조직 안에 넘길 사람이 없습니다 — 아래에서 검색해 지정하세요
                            </span>
                          )}
                        </label>
                        {config.action === OrgWithdrawalAction.TRANSFER && hasMembers && (
                          <select
                            value={
                              // 조직 밖에서 고른 대상은 이 목록에 없다 — 그 상태에서
                              // value를 그대로 두면 select가 첫 항목을 고른 것처럼 보인다
                              config.externalTarget ? '' : config.transferToMemberId || ''
                            }
                            onChange={(e) => {
                              const picked = members.find(
                                (m) => m.memberId === e.target.value,
                              );
                              updateOrgConfig(org.organization.id, {
                                transferToMemberId: picked?.memberId,
                                // 내부 멤버로 다시 고르면 조직 밖 선택은 버린다
                                externalTarget: undefined,
                              });
                            }}
                            className="ml-6 mt-2 w-full max-w-xs px-3 py-2 text-sm border border-gray-300 rounded-control focus:ring-2 focus:ring-brand-500"
                          >
                            <option value="">멤버 선택...</option>
                            {members.map((m) => (
                              <option key={m.memberId} value={m.memberId}>
                                {m.name}
                                {m.isOwner ? ` (${OWNER_LABEL})` : ''}
                                {m.leftAt ? ' · 퇴사' : ''}
                              </option>
                            ))}
                          </select>
                        )}
                        {config.action === OrgWithdrawalAction.TRANSFER && (
                          <>
                            {hasMembers && (
                              <p className="ml-6 mt-2 text-xs text-gray-500">
                                조직 밖 계정에도 넘길 수 있습니다.
                              </p>
                            )}
                            <TransferTargetSearch
                              excludeUserId={user.id}
                              selected={config.externalTarget}
                              onPick={(candidate) =>
                                updateOrgConfig(org.organization.id, {
                                  transferToMemberId: candidate.memberId,
                                  externalTarget: candidate,
                                })
                              }
                              onClear={() =>
                                updateOrgConfig(org.organization.id, {
                                  transferToMemberId: undefined,
                                  externalTarget: undefined,
                                })
                              }
                            />
                          </>
                        )}
                      </div>
                      {/* 폐쇄 옵션 */}
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name={`org-${org.organization.id}`}
                          checked={config.action === OrgWithdrawalAction.CLOSE}
                          onChange={() =>
                            updateOrgConfig(org.organization.id, {
                              action: OrgWithdrawalAction.CLOSE,
                              transferToMemberId: undefined,
                              externalTarget: undefined,
                            })
                          }
                          className="text-orange-600"
                        />
                        <span className="ml-2 text-sm text-orange-600">폐쇄</span>
                      </label>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* 강제 탈퇴 사유 유형 — 복원·재가입 가부를 가르므로 필수 */}
        <div>
          <label
            htmlFor="withdraw-reason-type"
            className="text-sm font-medium text-gray-700"
          >
            강제 탈퇴 사유 유형 <span className="text-red-600">*</span>
          </label>
          <select
            id="withdraw-reason-type"
            value={reasonType}
            onChange={(e) =>
              setReasonType(e.target.value as WithdrawalReasonType | '')
            }
            className="mt-2 w-full px-3 py-2 text-sm border border-gray-300 rounded-control focus:ring-2 focus:ring-brand-500"
          >
            <option value="">사유 유형 선택...</option>
            {REASON_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            {REASON_TYPE_OPTIONS.find((o) => o.value === reasonType)?.hint ??
              '유형에 따라 본인 복원 가능 여부와 재가입 차단이 달라집니다.'}
          </p>
        </div>

        {/* 본인 통지 — 기본 꺼짐(이 옵션 이전 동작이 무통지였다) */}
        <div>
          <label
            className={`flex items-start ${
              canNotify ? 'cursor-pointer' : 'opacity-60 cursor-not-allowed'
            }`}
          >
            <input
              type="checkbox"
              checked={canNotify && notifyUser}
              onChange={(e) => setNotifyUser(e.target.checked)}
              disabled={!canNotify}
              className="mt-0.5 text-brand-600"
            />
            <div className="ml-3">
              <span className="text-sm font-medium text-gray-900">
                본인에게 탈퇴 통지 보내기
              </span>
              <p className="text-xs text-gray-500 mt-0.5">
                {canNotify
                  ? '등록된 이메일·휴대폰으로 탈퇴 완료와 30일 복구 기한을 안내합니다.'
                  : reasonType === WithdrawalReasonType.SANCTION
                  ? '제재 탈퇴에는 보낼 수 없습니다 — 통지 문구가 안내하는 30일 본인 복구가 제재에는 적용되지 않습니다.'
                  : '사유 유형을 먼저 선택하세요. 정리(CLEANUP) 탈퇴에만 보낼 수 있습니다.'}
              </p>
            </div>
          </label>
        </div>

        {/* 사유 상세 (자유 입력) — 유형과 대체 관계가 아니다 */}
        <div>
          <label className="text-sm font-medium text-gray-700">사유 상세 (선택)</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="강제 탈퇴 사유를 입력하세요..."
            rows={2}
            maxLength={500}
            className="mt-2 w-full px-3 py-2 text-sm border border-gray-300 rounded-control focus:ring-2 focus:ring-brand-500 resize-none"
          />
        </div>

        {/* 에러 메시지 */}
        {error && (
          <div className="p-3 bg-red-50 text-red-600 text-sm rounded-card">{error}</div>
        )}

        {/* 액션 버튼 */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="secondary" onClick={onClose} disabled={isPending}>
            취소
          </Button>
          {/* 강제 탈퇴=warning — 슈퍼관리자가 되돌릴 수 있다(되돌릴 수 없는 하드삭제는 없어졌다) */}
          <Button
            variant="warning"
            onClick={handleSubmit}
            disabled={isPending || isConfirmDisabled}
          >
            {isPending ? '처리 중...' : '강제 탈퇴'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
